import { useState } from 'react';
import { Maximize2 } from 'lucide-react';

interface SizeSliderProps {
  id: string;
  label: string;
  description?: string;
  value: string | number;
  min: number;
  max: number;
  step: number;
  onChange: (value: string) => void;
  readOnly?: boolean;
}

export function SizeSlider({
  id,
  label,
  description,
  value,
  min,
  max,
  step,
  onChange,
  readOnly = false,
}: SizeSliderProps) {
  // Parse numeric value from rem/px string
  const parseValue = (val: string | number): number => {
    if (typeof val === 'number') return val;
    const numMatch = val.match(/^([\d.]+)/);
    return numMatch ? parseFloat(numMatch[1]) : min;
  };

  // Get unit from value string
  const getUnit = (val: string | number): string => {
    if (typeof val === 'number') return 'px';
    const unitMatch = val.match(/(rem|px|em)$/);
    return unitMatch ? unitMatch[1] : 'rem';
  };

  const [numericValue, setNumericValue] = useState(parseValue(value));
  const unit = getUnit(value);

  const handleSliderChange = (newValue: number) => {
    setNumericValue(newValue);
    onChange(`${newValue}${unit}`);
  };

  const handleInputChange = (newValue: string) => {
    const parsed = parseFloat(newValue);
    if (!isNaN(parsed) && parsed >= min && parsed <= max) {
      setNumericValue(parsed);
      onChange(`${parsed}${unit}`);
    }
  };

  return (
    <div className="token-input space-y-2">
      {/* Label */}
      <div>
        <label htmlFor={id} className="block text-sm font-medium text-gray-700">
          {label}
        </label>
        {description && (
          <p className="text-xs text-gray-500 mt-0.5">{description}</p>
        )}
      </div>

      {/* Slider + Value Input */}
      <div className="flex items-center space-x-3">
        <Maximize2 className="w-4 h-4 text-gray-400 flex-shrink-0" />

        {/* Slider */}
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={numericValue}
          onChange={(e) => handleSliderChange(parseFloat(e.target.value))}
          disabled={readOnly}
          className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
        />

        {/* Value Input */}
        <div className="flex items-center space-x-1 w-24">
          <input
            type="number"
            min={min}
            max={max}
            step={step}
            value={numericValue}
            onChange={(e) => handleInputChange(e.target.value)}
            disabled={readOnly}
            className="input w-full text-sm text-right pr-1"
          />
          <span className="text-sm text-gray-600 font-mono">{unit}</span>
        </div>
      </div>

      {/* Range Labels */}
      <div className="flex justify-between text-xs text-gray-500">
        <span>{min}{unit}</span>
        <span>{max}{unit}</span>
      </div>
    </div>
  );
}
